<script setup lang='ts'>
import debounce from 'lodash/debounce'
import omitBy from 'lodash/omitBy'
import isNil from 'lodash/isNil'
import { computed, onMounted, reactive, watch } from 'vue'
import { useIntervalFn } from '@vueuse/core'
import { useCustomization } from '@/hooks/useCustomization'
import { useEnhancer } from '@/app/enhancer'
import { randomConfig } from '@/constants/state'

const props = defineProps({
  contrastStyle: {
    type: String,
    default: () => '',
  },
})
const customization = useCustomization()
const { storeFavorites } = useEnhancer()
const state = reactive({
  model: null,
  frame: null,
  temple: null,
  len: null,
  bridge: null,
  logoFrontColor: null,
  logoBackColor: null,
})
let timeTriggerAutoMode: Date
const KEY_TURN_ON_AUTO_MODE_ON_MOUNTED = 'KEY_TURN_ON_AUTO_MODE_ON_MOUNTED'

const partIntervalCtx = useIntervalFn(() => {
  const frame = customization.getRandomByPart('frame')
  const len = customization.getRandomByPart('lens')
  const temple = customization.getRandomByPart('temple')
  const bridge = customization.getRandomByPart('bridge')
  const logoFrontColor = customization.getRandomByPart('logoFrontColor')
  const logoBackColor = customization.getRandomByPart('logoBackColor')
  Object.assign(state, {
    model: null,
    frame,
    temple,
    len,
    bridge,
    logoFrontColor,
    logoBackColor,
  })
}, randomConfig.timer.part, { immediate: false })

const modelIntervalCtx = useIntervalFn(() => {
  // ? When running auto mode, force refresh page after 60m to prevent out of memory error
  if (new Date().getTime() - timeTriggerAutoMode.getTime() > 60 * 60 * 1000) {
    window.sessionStorage.setItem(KEY_TURN_ON_AUTO_MODE_ON_MOUNTED, 'true')
    window.location.reload()
    return
  }
  const randomModel = customization.getRandomByPart('model')
  Object.assign(state, { model: randomModel })
}, randomConfig.timer.model, { immediate: false })

watch(() => state, debounce((newValue) => {
  storeFavorites.setInitial(omitBy(newValue, isNil))
}, 50), { deep: true })

const isActive = computed(() => storeFavorites.isSynced)
const textColor = computed(() => props.contrastStyle === 'white' ? 'dark' : 'white')

const triggerInterval = debounce(() => {
  if (isActive.value) {
    partIntervalCtx.resume()
    modelIntervalCtx.resume()
    timeTriggerAutoMode = new Date()
    return
  }
  partIntervalCtx.pause()
  modelIntervalCtx.pause()
}, 50)

const handleClick = () => {
  storeFavorites.setActiveSynced(!isActive.value)
  triggerInterval()
}

watch(() => storeFavorites.isSynced, (isSynced) => {
  !isSynced && triggerInterval()
})

onMounted(() => {
  if (window.sessionStorage.getItem(KEY_TURN_ON_AUTO_MODE_ON_MOUNTED) === 'true') {
    window.sessionStorage.removeItem(KEY_TURN_ON_AUTO_MODE_ON_MOUNTED)
    handleClick()
  }
})
</script>

<template>
  <q-btn
    :color="textColor"
    :text-color="contrastStyle"
    :outline="!isActive"
    round
    push
    dense
    size="16px"
    :label="isActive ? 'ON' : 'OFF'"
    @click="handleClick"
  />
  <span
    class="text-small text-shadow absolute full-width" :class="[`text-${contrastStyle}`]"
    style="bottom: -12px; left: 50%; transform: translate(-50%, 10px)"
    @click="handleClick"
  >
    AUTOモード
  </span>
</template>
